import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { ticketsApi } from './api';
import { deriveDiscoveryFlags, shouldRebroadcastBus } from './discovery-convergence';
import { queryKeys } from './query-keys';
import { useBeckonDraft } from './use-beckon-draft';

/**
 * How long a city-only station discovery may stay un-settled before the client gives up.
 *
 * Deliberately far longer than the default Beckn-TTL budget the booking steps use. A bus BPP
 * answers a city search with a PAGINATED catalog — an announce page followed by content pages
 * (DTC declares MAX_PAGE_NUMBER 28) — so the discovery is fulfilled by many callbacks arriving
 * over time, not one. The server holds the draft `pending` while pages are still landing and
 * resolves it to `completed` or `unavailable` on its own (its window restarts on each page
 * received), so it ALWAYS terminates; a short client budget just abandons a discovery the
 * server is still completing and reports "no operators answered" over a result that arrives
 * moments later. Observed on preprod: stations landed at 43s with the client already stopped.
 */
const DISCOVERY_POLL_TIMEOUT_MS = 180_000;

/**
 * Polls the metro booking draft for a transaction — a thin wrapper over the
 * generic useBeckonDraft (shared with rides). Stops when the current step
 * completes/errors; flags timedOut after `timeoutMs` (~30s by default).
 */
export function useTransitDraft(
  transactionId: string | null,
  timeoutMs?: number,
  continueAfterTimeout = false
) {
  return useBeckonDraft(
    transactionId,
    ticketsApi.draft,
    queryKeys.draft,
    timeoutMs,
    continueAfterTimeout
  );
}

/**
 * Catalog-driven station discovery for a city. On a server cache hit the station
 * list comes back inline ("ready"); on a miss the backend triggers a city-only
 * search1 and we poll the resulting draft until the BPP's catalog (stations)
 * arrives. The seed DB is no longer involved — stations carry the BPP's own codes.
 */
export function useStationDiscovery(
  cityCode: string | null,
  vehicleCategory: 'METRO' | 'BUS',
  // Chosen operator's bpp_id (from the operator picker). Empty for a single-operator city — the
  // backend then uses broadcast discovery. When set, discovery routes directly to that BPP.
  operatorBppId = '',
  autoDiscover = true
) {
  const discoverQuery = useQuery({
    queryKey: queryKeys.stations(cityCode ?? '', vehicleCategory, operatorBppId),
    queryFn: () => ticketsApi.stations(cityCode as string, vehicleCategory, operatorBppId, false),
    enabled: Boolean(cityCode) && autoDiscover,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  const rawTxnId = discoverQuery.data?.transactionId ?? null;
  const pollTxnId =
    discoverQuery.data?.status === 'discovering'
      ? rawTxnId
      : null;
  // Discovery gets the long budget — the catalog may arrive across many pages (see the const).
  const draft = useTransitDraft(pollTxnId, DISCOVERY_POLL_TIMEOUT_MS);

  const isReady = discoverQuery.data?.status === 'ready';
  const stations = isReady ? (discoverQuery.data?.stations ?? []) : (draft.draft?.stations ?? []);

  // Keep the latest known discovery transaction ID so it survives transitions
  // (e.g. when discovery draft completes and status becomes 'ready').
  const lastKnownTxnId = useRef<string | null>(null);
  useEffect(() => {
    lastKnownTxnId.current = null;
  }, [cityCode, vehicleCategory, operatorBppId]);

  const candidateTxnId = rawTxnId || draft.draft?.transactionId || null;
  if (candidateTxnId) {
    lastKnownTxnId.current = candidateTxnId;
  }
  const discoveryTransactionId = candidateTxnId || lastKnownTxnId.current;

  // The BPP that served the catalog. The follow-up fare search must route directly to
  // it (otherwise it broadcasts and the fare options carry no bpp_uri, breaking select).
  // A cache hit ("ready") carries it inline; a miss surfaces it on the polled draft.
  const bppId = (isReady ? discoverQuery.data?.bppId : draft.draft?.bppId) ?? '';
  const bppUri = (isReady ? discoverQuery.data?.bppUri : draft.draft?.bppUri) ?? '';

  const retryDiscovery = () => {
    return discoverQuery.refetch();
  };

  // The backend flips a city discovery to "unavailable" once its TTL elapses with no serving BPP — the
  // honest "no operators" terminal — and also immediately when a BUS seller answers with a non-bookable
  // "fare menu" instead of a station list.
  const rawUnavailable = draft.draft?.status === 'unavailable';

  // Bus: converge on the station list by re-broadcasting a bounded number of times before surfacing a real
  // "no operators". One retry per distinct discovery txn (guarded by lastRetriedTxn) so we never loop
  // within a single draft. Metro never retries — its "unavailable" is terminal.
  const [busRetries, setBusRetries] = useState(0);
  const lastRetriedTxn = useRef<string | null>(null);
  useEffect(() => {
    setBusRetries(0);
    lastRetriedTxn.current = null;
  }, [cityCode, vehicleCategory, operatorBppId]);
  useEffect(() => {
    if (
      shouldRebroadcastBus({
        vehicleCategory,
        rawUnavailable,
        stationsLen: stations.length,
        busRetries,
        txnId: pollTxnId,
        lastRetriedTxn: lastRetriedTxn.current,
      })
    ) {
      lastRetriedTxn.current = pollTxnId;
      setBusRetries((n) => n + 1);
      void retryDiscovery();
    }
    // retryDiscovery closes over stable refs + the query handle; intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleCategory, rawUnavailable, stations.length, busRetries, pollTxnId]);

  // While a bus discovery still has retry budget, stay in "discovering" (not "no operators").
  const { busRetrying, unavailable } = deriveDiscoveryFlags({
    vehicleCategory,
    rawUnavailable,
    busRetries,
  });

  const isLoading =
    discoverQuery.isLoading ||
    busRetrying ||
    (Boolean(pollTxnId) &&
      stations.length === 0 &&
      !unavailable &&
      !draft.timedOut &&
      draft.draft?.status !== 'error');

  return {
    stations,
    bppId,
    bppUri,
    // True when discovery settled with no serving operator for the city (TTL elapsed / all responders
    // dropped). The UI shows an honest "no operators available" empty state rather than a spinner.
    unavailable,
    // The search1 (city discovery) transaction, when discovery did a live broadcast
    // (miss). The booking/fare search reuses it so the catalog→station search pair
    // shares one transaction_id (ONDC TRV11 2-step search). Null on a cache hit.
    discoveryTransactionId,
    isLoading,
    error: discoverQuery.error ?? draft.error,
    timedOut: draft.timedOut,
    // Re-run discovery (cache hit → ready; miss → fresh search1) — used to recover
    // from a discovery timeout without forcing the user to switch cities.
    refetch: discoverQuery.refetch,
    // Force a FRESH broadcast discovery, bypassing the cached BPP — used by the "try another operator"
    // action. The caller must clear the chosen stations afterward (codes are BPP-specific).
    retryDiscovery,
  };
}
